import "./SVGGraphColumn.css";

import React, { useCallback, useEffect, useRef, useState } from "react";

import monitorApi from "../../../api/monitorApi";
import spinner from "../../../assets/img/spinner.svg";
import ColorLabel from "../../../components/ColorLabels/ColorLabels";
import SVGGraphColumnNav from "./SVGGraphColumnNav/SVGGraphColumnNav";
import {
  CollumnInfoType,
  SVGGraphColumnInputType,
} from "./SVGGraphColumnTypes";

export const COLUMN_TOOLTIP_DELAY_MS = 250;

type ColumnTooltipState = {
  value: number;
  x: number;
  y: number;
};

const SVGGraphColumn = ({
  display_type,
  title,
  request_url,
  data,
  collums_name_array,
  subtitle,
  counter,
  changeCounter,
  legend,
  allStatsData,
  currentYear,
  currentMonth,
  deltaCounter,
  dataType,
}: SVGGraphColumnInputType) => {
  const svgColumnObject = useRef<SVGSVGElement>(null);
  const [counter1, setCounter1] = useState<number | null>(null);
  const [maxWidth, setMaxWidth] = useState(700);
  const [maxAmount, setMaxAmount] = useState(0);
  const [svgInfo, setSVGInfo] = useState<Array<CollumnInfoType>>([]);
  const [leftOffset, setLeftOffset] = useState(30);
  const [isPadded, setIsPadded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [columnTooltip, setColumnTooltip] = useState<ColumnTooltipState | null>(
    null,
  );
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTooltipRef = useRef<{ value: number } | null>(null);
  const mousePositionRef = useRef({ x: 0, y: 0 });

  const clearTooltipTimeout = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  }, []);

  const handleBarMouseEnter = useCallback(
    (value: number, event: React.MouseEvent<SVGRectElement>) => {
      clearTooltipTimeout();
      mousePositionRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      pendingTooltipRef.current = { value };
      tooltipTimeoutRef.current = setTimeout(() => {
        setColumnTooltip({
          value: pendingTooltipRef.current!.value,
          ...mousePositionRef.current,
        });
      }, COLUMN_TOOLTIP_DELAY_MS);
    },
    [clearTooltipTimeout],
  );

  const handleBarMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      mousePositionRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      setColumnTooltip((previous) =>
        previous
          ? {
              ...previous,
              x: event.clientX,
              y: event.clientY,
            }
          : previous,
      );
    },
    [],
  );

  const handleBarMouseLeave = useCallback(() => {
    clearTooltipTimeout();
    pendingTooltipRef.current = null;
    setColumnTooltip(null);
  }, [clearTooltipTimeout]);

  useEffect(() => {
    return () => clearTooltipTimeout();
  }, [clearTooltipTimeout]);

  useEffect(() => {
    if (counter || counter === 0) {
      setCounter1(counter);
    }
  }, [counter]);

  // Use data directly if provided, otherwise make API call
  useEffect(() => {
    if (data !== undefined && data !== null) {
      // Use provided data directly
      setSVGInfo(data as Array<CollumnInfoType>);
      setIsPadded(false);
      setIsLoading(false);
    } else if (request_url) {
      // Fall back to API call for backward compatibility
      setIsLoading(true);
      let payload: any;
      if (counter1 !== null) {
        payload = {
          display_type: display_type,
          counter: counter1,
        };
      } else {
        payload = {
          display_type: display_type,
        };
      }

      monitorApi
        .postStats(request_url, payload)
        .then((response) => {
          setSVGInfo(response.data.info);
          setIsPadded(false);
          setIsLoading(false);
        })
        .catch((error) => {
          console.info(
            "There was an error fetching the statistics data!",
            error,
          );
          setIsLoading(false);
        });
    }
  }, [data, request_url, counter1, display_type]);

  const handle_collumn_title = useCallback(
    (column_title: number | string) => {
      let returnable_data = <tspan>{column_title}</tspan>;
      if (collums_name_array && typeof column_title === "number") {
        if (svgInfo.length <= collums_name_array.length) {
          returnable_data = <tspan>{collums_name_array[column_title]}</tspan>;
        }
      }
      return returnable_data;
    },
    [svgInfo, collums_name_array],
  );

  const handleColums = () => {
    const result = svgInfo.map((item: CollumnInfoType, index: number) => {
      return (
        <text
          key={index}
          className="fill-gray-label"
          textAnchor="middle"
          x={index * 36 + 8 + leftOffset}
          y={220}
        >
          {handle_collumn_title(item.title)}
        </text>
      );
    });
    return result;
  };

  useEffect(() => {
    let max_amount_in_effect = 0;
    svgInfo.forEach((column_value) => {
      if (column_value.value > max_amount_in_effect) {
        max_amount_in_effect = column_value.value;
      }
    });
    setMaxAmount(Math.ceil(max_amount_in_effect / 5) * 5);
  }, [svgInfo, collums_name_array]);

  useEffect(() => {
    if (collums_name_array && svgInfo.length > 0 && !isPadded) {
      if (collums_name_array.length > svgInfo.length) {
        const svg_1 = [...svgInfo];
        while (svg_1.length < collums_name_array.length) {
          svg_1.push({ value: 0, title: svg_1.length });
        }
        setSVGInfo(svg_1);
        setIsPadded(true);
      }
    }
  }, [collums_name_array, svgInfo, isPadded]);

  const shouldHighlightColumn = useCallback(
    (columnTitle: number | string) => {
      if (dataType !== "year") {
        return false;
      }
      if (currentYear === undefined || currentMonth === undefined) {
        return false;
      }

      const displayYear = currentYear + (counter ?? 0);
      return displayYear === currentYear && columnTitle === currentMonth;
    },
    [counter, currentMonth, currentYear, dataType],
  );

  useEffect(() => {
    setLeftOffset(maxAmount.toString().length * 8.5 + 30);
  }, [maxAmount]);

  useEffect(() => {
    setMaxWidth(svgInfo.length * 36 + leftOffset);
  }, [svgInfo.length, leftOffset]);

  return (
    <div className="svg_colums w-auto">
      <div>
        <h3 className="fw-800 font-bold text-2xl w-auto text-center mx-auto max-w-lg mb-1">
          {title ? title : ""}
        </h3>

        <SVGGraphColumnNav
          dataType={dataType}
          subtitle={subtitle}
          changeCounter={changeCounter}
          allStatsData={allStatsData}
          currentYear={currentYear}
          currentMonth={currentMonth}
          counter={counter}
          deltaCounter={deltaCounter}
        />
      </div>
      <div className="mx-auto overflow-x-auto overflow-y-hidden relative min-h-[230px] min-w-[200px]">
        {columnTooltip ? (
          <div
            className="svg-graph-column-tooltip"
            data-testid="svg-graph-column-tooltip"
            style={{
              left: columnTooltip.x + 12,
              top: columnTooltip.y + 12,
            }}
          >
            {columnTooltip.value}
          </div>
        ) : null}
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={spinner}
              alt="Loading..."
              width={32}
              height={32}
              className="animate-spin"
            />
          </div>
        ) : (
          <svg
            data-testid="svg-graph-column-chart"
            width={maxWidth}
            height="230"
            ref={svgColumnObject}
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${maxWidth} 220`}
          >
            <g>
              {svgInfo.map((item: CollumnInfoType, index: number) => {
                let height = maxAmount > 0 ? (item.value / maxAmount) * 170 : 0;
                if (!isFinite(height) || isNaN(height)) {
                  height = 0;
                }
                const yPosition = 190 - height;

                return (
                  <rect
                    key={index}
                    className={
                      shouldHighlightColumn(item.title)
                        ? "fill-bar-highlight"
                        : "fill-bar-default"
                    }
                    x={index * 36 + leftOffset}
                    y={yPosition}
                    rx="2"
                    ry="2"
                    width="16"
                    height={height}
                    onMouseEnter={(event) =>
                      handleBarMouseEnter(item.value, event)
                    }
                    onMouseMove={handleBarMouseMove}
                    onMouseLeave={handleBarMouseLeave}
                  />
                );
              })}
              {handleColums()}
            </g>
            {[0.2, 0.4, 0.6, 0.8, 1].map((value1, index) => {
              const label1 = value1 * maxAmount;
              const yPos =
                maxAmount > 0 ? 190 - (label1 / maxAmount) * 170 : 190;
              return (
                <text className="fill-gray-label" x={2} y={yPos} key={index}>
                  <tspan>{label1}</tspan>
                </text>
              );
            })}
          </svg>
        )}
      </div>
      {legend ? (
        <div className="max-w-lg mt-3.5">
          <ColorLabel color="#723509" text={legend} font_size={16} />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default SVGGraphColumn;
